
  import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
  import {bus} from "@/plugins/bus";
  import {IApp} from "@/interfaces/app.interface";
  import {IReport} from "@/modules/Report/interfaces/report.interface";
  import {IBook} from "@/interfaces/book.interface";
  import {KeyboardMobileHidden} from "@/plugins/keyboard-mobile-hidden";
  import {IAuth} from "@/modules/Auth/interfaces/auth.interface";
  import _ from "lodash";
  import SelectParks from "@/components/SpecialFilters/SelectParks.vue";
  import {IAppTable} from "@/shared/AppTable/interfaces/sort-table-data";
  import AppDatePicker from "@/components/FormControls/AppDatePicker.vue";
  import {cookieBannerHelper} from "@/shared/CookieBanner/helpers/cookie-banner-helper";
  import {IAppState} from "@/interfaces/app-state.interface";
  import AppCorpsFilter from "@/components/FormControls/AppCorpsFilter.vue";

  @Component({
    components: {AppCorpsFilter, AppDatePicker, SelectParks}
  })
  export default class ReportFilters extends Vue {
    $refs: any;
    dateFormats = IApp.DateFormats;
    publicPath = process.env.VUE_APP_PUBLIC_URL;
    format = IApp.DateFormats.PrintedDate;
    reportName = IReport.ReportName;
    defaultValues: IReport.IFilters = {...IReport.DefaultFilters};
    formModel: IReport.IFilters = {...IReport.DefaultFilters};
    tableWidth = 1440;
    bookName = IBook.BookName;
    resizeTimer = null;
    visibleScenarioDropdown = false;
    visiblePrintDropdown = false;
    fakeReadOnly = true;
    hours = [];

    get storeState(): IAppState { return this.$store.state; }
    get userAccess(): IAuth.UserAccess { return this.storeState.auth.userAccess; }
    get activeReport() { return this.storeState.report.activeReport; }
    get filterEmpty() {
      const fields = ['corpId', 'str', 'dateBeg', 'dateEnd', 'balanceTypeId', 'payFlag'];
      const arrays = ['parks', 'hours', 'groupTypes', 'cameras'];

      return fields.every(field => !this.formModel[field])
        && arrays.every(arr => !this.formModel[arr].length);
    }

    get books() { return this.storeState.book.books; }
    get dateEmpty() { return !Boolean(this.formModel.dateBeg) && !Boolean(this.formModel.dateEnd); }
    get displayDetails() { return this.storeState.report.displayDetails; }
    get tableTotal(): IReport.ITableTotal { return this.storeState.report.tableTotal; }
    get parksBook(): IBook.ParkBookItem[] { return this.$store.getters['book/parksBook']; }

    get groupingParks() {
      let storageValue = localStorage.getItem(IApp.LocalStorageKeys.SelectParksGrouping);
      return storageValue && cookieBannerHelper.cookieConfig.optional ? JSON.parse(storageValue) : true;
    }
    get filteredCameras(): IBook.CamerasBookItem[] {
      let result = this.books[this.bookName.cameras];
      const parkPropName = this.groupingParks ? 'globalParkId' : 'parkId';
      if (this.formModel.parks && this.formModel.parks.length) {
        result = result.filter(book => this.formModel.parks.includes(book[parkPropName]));
      }

      // For Transition from the barriers page, need filter by camera type.
      if (this.$route.params.movId) {
        result = result.filter(book => book.typeId === 1);
      }
      return result;
    }

    created() {
      this.formModel = { ...this.defaultValues };
      for (let i = 0; i < 24; i++) this.hours.push(i);

      if (this.storeState.report.filters) {
        Vue.set(this, 'formModel', { ...this.storeState.report.filters });
      }

      bus.$on(IReport.EmitterEvents.CLEAR_FILTERS, () => {
        this.clearFilters();
      });

      KeyboardMobileHidden();
    }

    async mounted() {
      await this.$nextTick();
      this.calcTableWidth();

      window.addEventListener('resize', this.handleResize);
    }

    async onFilterClick () {
      let storageForm = localStorage.getItem(IReport.LocalStorageKeys.ReportFilters);
      let presetFilters = !!storageForm ? JSON.parse(storageForm) : null;
      let savedFilters: any = {};
      let formModelForSave = {...this.formModel};
      if (formModelForSave.searchFlag) delete formModelForSave.searchFlag;
      if (presetFilters) savedFilters = {...presetFilters};
      savedFilters = {
        ...savedFilters,
        [this.storeState.report.activeReport]: {...formModelForSave},
      }
      localStorage.setItem(IReport.LocalStorageKeys.ReportFilters, JSON.stringify(savedFilters));
      IAppTable.ScrollTableToTop();

      await this.$store.dispatch('report/getReport', {
        reportName: this.storeState.report.activeReport,
        filters: this.formModel,
      });
    }

    async clearFilters() {
      this.$store.commit('report/setProperty', { name: 'filters', value: _.cloneDeep(IReport.DefaultFilters) });
      this.$store.commit('report/setProperty', { name: 'defaultFilters', value: _.cloneDeep(IReport.DefaultFilters) });
      this.$store.commit('report/setProperty', { name: 'displayDetails', value: false });
      this.formModel = { ...this.defaultValues };
      await this.onFilterClick();
    }

    downloadExcel() {
      this.visiblePrintDropdown = false;
      this.$emit('downloadExcel');
    };

    printTable() {
      this.visiblePrintDropdown = false;
      this.$emit('printTable');
    };

    calcTableWidth() {
      let table = document.querySelector('.report-table');
      if (table) {
        this.tableWidth = table.clientWidth;
      }
    }

    setScenario(scenario: IBook.ScenarioBookItem) {
      this.visibleScenarioDropdown = false;
      this.formModel.str = scenario.description;
    }

    pickerShortcutAction(dates: { start?: string; end?: string; }) {
      if (dates.start) this.formModel.dateBeg = dates.start;
      if (dates.end) this.formModel.dateEnd = dates.end;
    }

    handleResize() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = setTimeout(() => {
        this.calcTableWidth();
      }, 150);
    }

    handleSelectVisible(visible) {
      if (visible) {
        // Когда селект открывается, находим внутренний input и отключаем автокомплит
        this.$nextTick(() => {
          const input = this.$refs.groupTypesSelect.$el.querySelector('input');
          if (input) {
            input.setAttribute('autocomplete', 'off');
            input.setAttribute('autocorrect', 'off');
            input.setAttribute('autocapitalize', 'off');
            input.setAttribute('spellcheck', 'false');

            // Самый радикальный способ - очистить историю ввода
            input.readOnly = true;
            setTimeout(() => {
              input.readOnly = false;
            }, 100);
          }
        });
      }
    }

    beforeDestroy() {
      window.removeEventListener('resize', this.handleResize);
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
    }

    blurCorp({ event, list }) {
      if (!event.target.value) return;
      let enteredValue = event.target.value.toString();
      let bookItem = list.find(item => item.name.toLowerCase().includes(enteredValue));
      if (bookItem) {
        this.formModel.corpId = bookItem.id;
      }
    }

    changeCorp(bookValue) {
      this.formModel.corpName = !!bookValue ? bookValue.name : '';
    }

    destroyed() {
      bus.$off(IReport.EmitterEvents.CLEAR_FILTERS);
    }

    @Watch('displayDetails')
    onDisplayDetailsChange() {
      Vue.set(this, 'formModel', { ...this.storeState.report.filters });
    }
  }
